
import React, { useRef, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Popup from "../components/popup.js";
  import recright from "../assets/Home/recright.webp";
// Import images from Managed folder (reusing existing images)
import useCase1Img from "../assets/Managed/man1.webp";
import useCase2Img from "../assets/Managed/man2.webp";
import useCase3Img from "../assets/Managed/man3.webp";
import useCase4Img from "../assets/Managed/man4.webp";
import useCase5Img from "../assets/Managed/man6.webp";
import bubble1 from "../assets/Managed/bubble1.webp";
import line from "../assets/Managed/line.webp";
import triangle from "../assets/Managed/triangle.webp";

const UseCases = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    // Set page title
    document.title = "Random Walk | Real-World AI Use Cases";

    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Discover how organizations across industries are leveraging our AI platforms to solve complex challenges and achieve remarkable results. Real transformational stories of AI in action."
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Discover how organizations across industries are leveraging our AI platforms to solve complex challenges and achieve remarkable results. Real transformational stories of AI in action.";
      document.head.appendChild(meta);
    }

    // Add or update Open Graph title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute("content", "Random Walk | Real-World AI Use Cases");

    // Add or update Open Graph description
    let ogDescription = document.querySelector(
      'meta[property="og:description"]'
    );
    if (!ogDescription) {
      ogDescription = document.createElement("meta");
      ogDescription.setAttribute("property", "og:description");
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute(
      "content",
      "Discover how organizations across industries are leveraging our AI platforms to solve complex challenges and achieve remarkable results."
    );

    // Add viewport meta tag
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement("meta");
      viewport.name = "viewport";
      document.head.appendChild(viewport);
    }
    viewport.setAttribute("content", "width=device-width, initial-scale=1");

    // Add theme-color meta tag
    let themeColor = document.querySelector('meta[name="theme-color"]');
    if (!themeColor) {
      themeColor = document.createElement("meta");
      themeColor.name = "theme-color";
      document.head.appendChild(themeColor);
    }
    themeColor.setAttribute("content", "#000000");

    // Add Open Graph image
    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement("meta");
      ogImage.setAttribute("property", "og:image");
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute("content", `${process.env.PUBLIC_URL}/rw_logo.png`);

    // Add Open Graph type
    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) {
      ogType = document.createElement("meta");
      ogType.setAttribute("property", "og:type");
      document.head.appendChild(ogType);
    }
    ogType.setAttribute("content", "website");

    // Add canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = "https://randomwalk.ai/use-cases/";
  }, []);

  const useCases = [
    {
      id: 1,
      title: "Predictive Quality Control in Manufacturing",
      subtitle: "Powered by: Visual AI + Analytical AI + Functional AI",
      para:"A global manufacturing firm needed to identify defects earlier in the production lifecycle and reduce losses from machine inefficiency. AI was deployed to integrate computer vision, analytics, and automation across assembly lines.",
      industry: "Government",
      image: useCase1Img,
      challenge:
        "Defects often went undetected until late-stage quality checks, leading to rework and high wastage. Manual inspections were inconsistent, and root-cause analysis was delayed due to fragmented data streams.",
      solution:
        "Visual AI captured real-time video feeds, identifying micro-defects in materials using deep convolutional models. Analytical AI correlated these visual patterns with machine telemetry to pinpoint recurring anomalies. Functional AI automated alerts and scheduled preventive maintenance based on predicted failure likelihood. The system created a live feedback loop between production, quality, and operations.",
      results: [
        "Achieved 35% reduction in product defects, 27% improvement in production uptime, and optimized maintenance schedules that reduced machine downtime by half.",
      ],
      readMoreLink: "#",
      demoLink: "Request Demo",
    },
    {
      id: 2,
      title: "Enterprise Knowledge Continuum",
      subtitle: "Knowledge Nudge + Chateleon + AI Readiness Assessment + Fortune Cookie",
      industry: "Retail",
      para:"A global consulting organization sought to unify learning, communication, and performance insights across its multi-country operations. The Business Enterprise Suite became the backbone of their capability intelligence ecosystem.",
      image: useCase2Img,
      challenge:
        "Despite heavy investment in digital training, knowledge retention and internal communication were inconsistent. Leaders lacked a unified view of workforce readiness and could not quantify training ROI.",
      solution:
        "The organization conducted a company-wide AI Readiness Assessment to map digital competency gaps. Knowledge Nudge transformed static learning material into adaptive micro-assessments, reinforcing learning over time. Chateleon offered an intelligent conversational interface for on-demand guidance and content discovery. Fortune Cookie delivered predictive insights through a personalized data feed, helping leadership track learning outcomes in real time. Together, these platforms turned data into a continuous feedback and intelligence loop.",
      results: [
        "A 62% increase in training effectiveness, 40% faster decision-making, and measurable growth in digital capability across business units — all achieved without replacing existing systems.",
        ,
      ],
      readMoreLink: "#",
      demoLink: "Request Demo",
    },
    {
      id: 3,
      title: "Cognitive Document Intelligence for Financial Operations",
      subtitle: "Powered by: Textual AI + Analytical AI + Functional AI",
      industry: "Corporate Training",
         para:"A multinational financial institution aimed to automate the analysis of complex loan applications, contracts, and compliance reports at scale.",
      image: useCase3Img,
      challenge:
        "Teams spent days extracting and validating data from diverse, unstructured document formats — slowing loan disbursal and compliance checks. Manual oversight introduced human error and regulatory risk.",
      solution:
        "Textual AI applied OCR and NLP to extract structured data from scanned documents, contracts, and forms. Analytical AI validated extracted values against policy benchmarks and identified missing or inconsistent data. Functional AI routed flagged cases to human reviewers and auto-approved clean applications, closing the loop between data extraction and decisioning.",
      results: [
        "Reduced document processing time by 70%, improved compliance accuracy to 99.4%, and enabled real-time dashboards for risk officers to monitor portfolio health.",
       
      ],
      readMoreLink: "#",
      demoLink: "Request Demo",
    },
    {
      id: 4,
      title: "Intelligent Infrastructure for Smart Cities",
      subtitle: "Powered by: Visual AI + Analytical AI + Functional AI + Conversational AI",
      industry: "Law Enforcement",
      image: useCase4Img,
      para:"A metropolitan administration deployed an AI-driven operational intelligence system to improve civic response and optimize infrastructure management.",
      challenge:
        "Departments operated in isolation - road, waste, and traffic data were collected separately with minimal real-time coordination. Public grievance response times were long, and resource allocation lacked data-driven visibility.",
      solution:
        "Visual AI monitored traffic density, waste levels, and infrastructure integrity through live feeds and satellite imagery. Analytical AI processed millions of datapoints from IoT sensors to predict congestion, waste overflow, and utility strain. Functional AI automated task assignments for field teams, while Conversational AI enabled citizens to report issues via natural language interfaces on WhatsApp and city apps. All departments operated through a unified dashboard integrating visual, textual, and operational data.",
      results: [
        "Response time to civic issues decreased by 45%, operational efficiency improved by 38%, and citizen satisfaction rose sharply through transparent and interactive governance.",
     
      ],
      readMoreLink: "#",
      demoLink: "Request Demo",
    },
    {
      id: 5,
      title: "Optimizing Political Campaign Strategy with Voice Analytics",
      subtitle: "Powered by Speech-to-Text AI",
      industry: "Political Campaigns",
      image: useCase5Img,
      challenge:
        "A political party needed a way to understand public sentiment across diverse voter demographics. Traditional polling methods were slow, expensive, and often inaccurate in capturing real-time voter concerns.",
      solution:
        "Random Walk implemented an AI-driven platform that analyzes phone campaigns, town hall meetings, and social media interactions to gauge voter sentiment. The AI transcribes and processes speech from thousands of conversations, identifying trending topics, key concerns, and sentiment shifts across different regions.",
      results: [
        "The party was able to tailor its messaging and focus campaign resources more effectively",
        "Campaign strategists identified and addressed voter concerns 4x faster than traditional methods, resulting in a noticeable uptick in approval ratings during the campaign period",
      ],
      readMoreLink: "#",
      demoLink: "Request Demo",
    },
  ];

  const handleOpenPopup = () => {
    setIsPopupOpen(true);
  };

  return (
    <section className="Main2 font-display mt-16 md:mt-0 bg-[#f8f9fa]">
      {/* Hero Section */}
      <section className="bg-custom-manage min-h-[400px] md:min-h-[500px] flex justify-center items-center px-4 py-16 md:py-24">
        <article className="max-w-screen-xl w-full text-center">
          <h1 className="text-3xl md:text-5xl lg:text-6xl 2xl:text-7xl font-bold text-[#666666] mb-6">
           Intelligence in Action
          </h1>
          <p className="text-base md:text-lg lg:text-xl 2xl:text-2xl text-[#666666] max-w-4xl mx-auto leading-relaxed">
         AI creates value only when it’s applied - to real challenges, with measurable results. We build bespoke AI solutions that power tangible transformation. From predictive analytics to learning ecosystems and intelligent automation, our platforms are redefining how businesses operate, decide, and grow.

          </p>
        </article>
      </section>

      {/* Use Cases List */}
      <section className="max-w-screen-xl mx-auto px-4 md:px-8 lg:px-16 py-12 md:py-16 lg:py-24">
        {useCases.map((useCase, index) => (
          <article
            key={useCase.id}
            className={`mb-16 md:mb-24 lg:mb-32 ${
              index % 2 === 0 ? "md:flex-row-reverse" : "md:flex-row"
            } flex flex-col md:flex md:gap-8 lg:gap-12`}
          >
            {/* Image Section */}
            <figure className="w-full md:w-1/2 mb-6 md:mb-0 relative items-center flex justify-center">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src={useCase.image}
                  alt={useCase.title}
                  className="w-full h-64 md:h-80 lg:h-screen object-cover transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent"></div>
              </div>
              {/* Decorative element */}
          
            </figure>

            {/* Content Section */}
            <div className="w-full md:w-1/2 flex flex-col justify-center">
              <div className="bg-white p-6 md:p-8 lg:p-10 rounded-2xl shadow-xl border border-gray-200">
                {/* Industry Tag */}
               
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#666666] mb-3">
                  {useCase.title}
                </h2>
                <p className="text-sm md:text-base text-[#666666] mb-6 italic">
                  {useCase.subtitle}
                </p>
                <p className="text-sm md:text-base text-[#666666] mb-6 text-center">
                  {useCase.para}
                </p>

                {/* Challenge */}
                <div className="mb-6">
                  <h3 className="text-lg md:text-xl font-semibold text-[#3e59b7] mb-2 flex items-center">
                    <span className="mr-2">⚡</span> Challenge
                  </h3>
                  <p className="text-sm md:text-base text-[#666666] leading-relaxed">
                    {useCase.challenge}
                  </p>
                </div>

                {/* Solution */}
                <div className="mb-6">
                  <h3 className="text-lg md:text-xl font-semibold text-[#3e59b7] mb-2 flex items-center">
                    <span className="mr-2">💡</span> Solution
                  </h3>
                  <p className="text-sm md:text-base text-[#666666] leading-relaxed">
                    {useCase.solution}
                  </p>
                </div>

                {/* Results */}
                <div className="mb-6">
                  <h3 className="text-lg md:text-xl font-semibold text-[#3e59b7] mb-2 flex items-center">
                    <span className="mr-2">📈</span> Results
                  </h3>
                  <ul className="space-y-2">
                    {useCase.results.map((result, resultIndex) => (
                      <li
                        key={resultIndex}
                        className="text-sm md:text-base text-[#666666] flex items-start"
                      >
                        <span className="text-[#3e59b7] mr-2 mt-1">▸</span>
                        <span className="leading-relaxed">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mt-8">
                  <button
                    onClick={handleOpenPopup}
                    className="bg-[#3e59b7] hover:bg-[#1976D2] text-white px-6 py-3 rounded-lg text-sm md:text-base font-semibold transition-colors duration-300 flex items-center justify-center"
                  >
                    Read Full Case Study
                    <span className="ml-2">→</span>
                  </button>
                  <button
                    onClick={handleOpenPopup}
                    className="border-2 border-[#3e59b7] text-[#3e59b7] hover:bg-[#3e59b7] hover:text-white px-6 py-3 rounded-lg text-sm md:text-base font-semibold transition-colors duration-300"
                  >
                    {useCase.demoLink}
                  </button>
                </div>
              </div>
            </div>
          </article>
        ))}
      </section>
    <section
          className="text-[#666666] min-h-[180px] md:min-h-[230px] relative mt-20 bg-no-repeat bg-cover"
          style={{
            background: "linear-gradient(100deg , #3E59B7 90%, #A6BAFF 110%)", // Example pink gradient
          }}
        >
          <figure className="absolute bottom-0 right-0">
            <img
              src={recright}
              alt="Additional"
              loading="lazy"
              className="lg:w-40 lg:h-auto w-24 h-36 md:w-40 md:h-full"
            />
          </figure>
          <article className="absolute  inset-0 flex flex-col justify-center items-center text-center text-white pt-4 md:pt-10 md:mr-32 lg:pt-5 md:ml-20 ">
            <h2 className="md:text-xl lg:text-2xl text-sm font-bold mb-4 font-display">
           Ready to Experience Our AI Platforms?
            </h2>
            <p className="md:text-lg lg:text-lg text-sm font-light mb-4 font-display mx-2 ">
          Discover how our comprehensive AI platform suite can transform your <br/>enterprise operations and drive unprecedented growth.
            </p>

       <div className="flex gap-4 ">
  <button
    className="md:mt-3 mb-3 rounded-xl border text-[#3E59B7] bg-white md:py-3 md:px-20 px-8 py-2 transition-all duration-300 ease-in-out hover:bg-transparent hover:border hover:border-white hover:text-[#FFFFFF]"
    onClick={handleOpenPopup}

  >
    <div className="flex items-center">
      <span className="text-xs md:text-lg font-bold">Start Free Trial</span>
    </div>
  </button>

  <button
    className="md:mt-3 mb-3 rounded-xl border text-white border-white md:py-3 md:px-20 px-8 py-2 transition-all duration-300 ease-in-out bg-transparent hover:bg-white hover:text-[#3E59B7]"
   onClick={handleOpenPopup}

  >
    <div className="flex items-center">
      <span className="text-xs md:text-lg font-bold">Schedule Demo
      </span>
    </div>
  </button> 
</div>
          </article>
        </section>
      {/* Call to Action Section */}


      {/* Popup Section */}
      {isPopupOpen && (
        <Popup
          utmContent={"use-cases"}
          isModalOpen={isPopupOpen}
          setIsModalOpen={setIsPopupOpen}
        />
      )}
    </section>
  );
};

export default UseCases;
